// src/components/fees/FeeAllocate.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Grid, Skeleton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert,
  TablePagination, Autocomplete
} from '@mui/material';
import { 
  Add as AddIcon, 
  PictureAsPdf as PdfIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { feeAPI, studentAPI, academicAPI } from '../../services/api';
import StudentSelectionDialog from './StudentSelectionDialog';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const FeeAllocate = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.FEES.ALLOCATIONS, ACTIONS.READ);
  const canCreate = can(RESOURCES.FEES.ALLOCATIONS, ACTIONS.CREATE);
  const [feeGroups, setFeeGroups] = useState([]);
  const [terms, setTerms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [formData, setFormData] = useState({
    student_id: '',
    fee_group_id: '',
    due_date: new Date().toISOString().split('T')[0]
  });
  const [allocating, setAllocating] = useState(false);
  const [result, setResult] = useState(null);
  
  // Student search
  const [studentSearchOptions, setStudentSearchOptions] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  
  // Pagination and filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    fee_group_id: '',
    term_id: '',
    status: ''
  });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (canRead) fetchData();
  }, [canRead]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, termsRes] = await Promise.all([
        feeAPI.getGroups({ per_page: 1000 }),
        academicAPI.getTerms()
      ]);
      
      setFeeGroups(Array.isArray(groupsRes.data) ? groupsRes.data : groupsRes.data.data || []);
      setTerms(Array.isArray(termsRes.data) ? termsRes.data : termsRes.data.data || []);
      
      await fetchAllocations();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllocations = async (pageNum = page, filterParams = filters, perPage = pageSize) => {
    try {
      const params = { page: pageNum, per_page: perPage, ...filterParams };
      const response = await feeAPI.getAllocations(params);
      const data = response.data.data || response.data;
      
      setAllocations(Array.isArray(data) ? data : []);
      setTotalPages(response.data.last_page || 1);
      setTotalRecords(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching allocations:', error);
      setAllocations([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAllocating(true);
    setResult(null);
    
    try {
      const response = await feeAPI.allocateToStudent(formData);
      setResult({
        type: 'success',
        message: response.data.message || 'Fees allocated successfully!'
      });
      fetchAllocations();
      
      setTimeout(() => {
        setFormData({ 
          student_id: '', 
          fee_group_id: '', 
          due_date: new Date().toISOString().split('T')[0]
        });
        setSelectedStudent(null);
        setStudentSearchInput('');
        setShowForm(false);
        setResult(null);
      }, 2000);
    } catch (error) {
      setResult({
        type: 'error',
        message: error.response?.data?.message || 'Allocation failed. Please try again.'
      });
    } finally {
      setAllocating(false);
    }
  };

  const searchStudents = async (searchTerm) => {
    setStudentSearchInput(searchTerm);
    
    if (!searchTerm || searchTerm.length < 2) {
      setStudentSearchOptions([]);
      return;
    }

    setStudentSearchLoading(true);
    try {
      const response = await studentAPI.search({ search: searchTerm });
      const students = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStudentSearchOptions(students);
    } catch (error) {
      console.error('Error searching students:', error);
      setStudentSearchOptions([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setFormData({ ...formData, student_id: student?.id || '' });
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    setPage(1);
    fetchAllocations(1, newFilters, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    fetchAllocations(1, filters, newPageSize);
  };

  const handleClearFilters = () => {
    const clearedFilters = { fee_group_id: '', term_id: '', status: '' };
    setFilters(clearedFilters);
    setPage(1);
    fetchAllocations(1, clearedFilters, pageSize);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchAllocations(newPage, filters, pageSize);
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const response = await feeAPI.downloadAllocationsPdf(filters);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fee_allocations_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const hasActiveFilters = filters.fee_group_id || filters.term_id || filters.status;

  if (!canRead) return <Alert severity="error">You do not have permission to view fee allocations.</Alert>;

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={400} />;
  }

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>Fee Allocation</Typography>
            <Typography variant="body1" color="text.secondary">
              Allocate fees to individual students or bulk allocate to multiple students
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} mt={{ xs: 2, md: 0 }}>
              {canRead && <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPdf} disabled={downloading}>{downloading ? 'Downloading...' : 'PDF'}</Button>}
              {canCreate && <Button variant="contained" onClick={() => setShowBulkDialog(true)} color="primary">Bulk Allocate</Button>}
              {canCreate && (
                <Button variant={showForm ? "outlined" : "contained"} startIcon={<AddIcon />} onClick={() => setShowForm(!showForm)}>
                  {showForm ? 'Cancel' : 'Single Student'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Bulk Allocation Dialog */}
      <StudentSelectionDialog
        open={showBulkDialog}
        onClose={() => setShowBulkDialog(false)}
        onAllocate={() => fetchAllocations()}
      />

      {/* Individual Student Allocation Dialog */}
      <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Allocate Fee to Individual Student</DialogTitle>
        <DialogContent>
          {result && (
            <Alert severity={result.type} sx={{ mb: 2 }}>
              {result.message}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <Autocomplete
              fullWidth
              options={Array.isArray(studentSearchOptions) ? studentSearchOptions : []}
              value={selectedStudent}
              inputValue={studentSearchInput}
              onChange={(event, newValue) => handleStudentSelect(newValue)}
              onInputChange={(event, newInputValue) => searchStudents(newInputValue)}
              loading={studentSearchLoading}
              getOptionLabel={(option) => option ? `${option.registration_no} - ${option.full_name}` : ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {option.registration_no}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.full_name} ({option.class_stream_name})
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Student"
                  placeholder="Type name or registration number..."
                  required
                  margin="normal"
                  helperText="Type at least 2 characters to search"
                />
              )}
              noOptionsText={studentSearchInput.length < 2 ? "Type at least 2 characters" : "No students found"}
              loadingText="Searching..."
              filterOptions={(x) => x}
            />

            <FormControl fullWidth required disabled={!selectedStudent} margin="normal">
              <InputLabel>Fee Group</InputLabel>
              <Select
                value={formData.fee_group_id}
                label="Fee Group"
                onChange={(e) => setFormData({...formData, fee_group_id: e.target.value})}
              >
                {feeGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {group.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        UGX {group.total_amount?.toLocaleString()} - {group.term?.name} {group.term?.year}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              required
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForm(false)} disabled={allocating}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={allocating || !formData.student_id || !formData.fee_group_id || !formData.due_date}
          >
            {allocating ? 'Allocating...' : 'Allocate Fees'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.fee_group_id}
                  onChange={(e) => handleFilterChange('fee_group_id', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Fee Groups</MenuItem>
                  {feeGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.term_id}
                  onChange={(e) => handleFilterChange('term_id', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Terms</MenuItem>
                  {terms.map((term) => (
                    <MenuItem key={term.id} value={term.id}>
                      {term.name} {term.year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  fullWidth
                  size="small"
                >
                  Clear
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Fee Allocations ({totalRecords})</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Reg No</TableCell>
                  <TableCell>Fee Type</TableCell>
                  <TableCell>Fee Group</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No allocations yet</TableCell>
                  </TableRow>
                ) : (
                  allocations.map((allocation) => (
                    <TableRow key={allocation.id} hover>
                      <TableCell>{allocation.student?.last_name} {allocation.student?.first_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {allocation.student?.registration_no}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={allocation.fee_type?.name} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{allocation.fee_group?.name}</TableCell>
                      <TableCell>{allocation.term?.name} {allocation.term?.year}</TableCell>
                      <TableCell>UGX {allocation.amount?.toLocaleString()}</TableCell>
                      <TableCell>{new Date(allocation.due_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={allocation.status}
                          size="small"
                          color={
                            allocation.status === 'paid' ? 'success' :
                            allocation.status === 'partial' ? 'warning' : 'error'
                          }
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalRecords}
            page={page - 1}
            onPageChange={(event, newPage) => handlePageChange(event, newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 15, 20, 50, 100]}
            labelRowsPerPage="Rows per page"
            showFirstButton={true}
            showLastButton={true}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeeAllocate;